import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Wallet, Clock, CalendarCheck, ShieldCheck, BadgeCheck, ArrowRight, Sparkles } from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import Seo from "../components/Seo";
import ReferentApplyForm from "../components/ReferentApplyForm";

const BENEFITS = [
  { icon: Wallet, title: "Générez des revenus", text: "Réalisez des missions rémunérées près de chez vous, en complément de votre activité." },
  { icon: CalendarCheck, title: "En toute liberté", text: "Vous choisissez vos missions, vos zones d'intervention et vos disponibilités." },
  { icon: ShieldCheck, title: "Paiement sécurisé", text: "Vous êtes réglé via la plateforme, avec des relevés mensuels et une facturation simplifiée." },
  { icon: MapPin, title: "Près de chez vous", text: "Intervenez sur des biens de votre secteur, sans déplacements inutiles." },
];

const PROFILES = ["Agents immobiliers", "Artisans du bâtiment", "Retraités actifs", "Auto-entrepreneurs", "Étudiants sérieux", "Gestionnaires indépendants"];

const STEPS = [
  { n: "1", title: "Vous candidatez", text: "Remplissez le formulaire avec votre profil, vos zones et les missions que vous savez réaliser." },
  { n: "2", title: "Nous validons", text: "Notre équipe étudie votre candidature et vérifie votre profil avant de l'activer." },
  { n: "3", title: "Vous recevez des missions", text: "Les bailleurs de votre secteur vous proposent des missions que vous acceptez librement." },
  { n: "4", title: "Vous êtes rémunéré", text: "Vous réalisez la mission, déposez le compte rendu, et êtes payé via la plateforme." },
];

const FAQ = [
  { q: "Faut-il un diplôme pour devenir référent ?", a: "Non. Nous recherchons des personnes sérieuses, fiables et disponibles. Un profil professionnel de l'immobilier ou du bâtiment est un plus, mais n'est pas obligatoire." },
  { q: "Combien puis-je gagner ?", a: "Votre rémunération dépend du nombre et du type de missions que vous acceptez. Vous êtes libre d'en prendre autant que vous le souhaitez, selon vos disponibilités." },
  { q: "Suis-je obligé d'accepter toutes les missions ?", a: "Non. Vous choisissez les missions qui vous conviennent, dans les zones que vous avez définies. Aucune obligation de volume." },
  { q: "Comment suis-je payé ?", a: "Les paiements passent par la plateforme, de façon sécurisée. Vous recevez des relevés mensuels qui simplifient votre facturation." },
];

export default function DevenirReferent() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
  const scrollToForm = () => document.getElementById("candidature")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white" data-testid="devenir-referent-page">
      <Seo
        title="Devenir référent local : générez des revenus près de chez vous | 360immoinvest"
        description="Rejoignez le réseau de référents locaux 360immoinvest : réalisez des états des lieux, remises de clefs et visites rémunérés, près de chez vous, quand vous voulez."
        path="/devenir-referent"
        jsonLd={faqJsonLd}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-brand-greenDeep relative overflow-hidden">
          <div className="absolute top-10 right-10 w-72 h-72 bg-brand-lime/10 rounded-full blur-3xl" />
          <div className="max-w-5xl mx-auto px-5 lg:px-8 relative">
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-brand-lime bg-white/10 px-3 py-1.5 rounded-full"><Sparkles className="w-3.5 h-3.5" /> Réseau de référents locaux</span>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-white mt-5 leading-tight max-w-3xl">Devenez référent local et générez des revenus près de chez vous</h1>
            <p className="mt-5 text-lg text-white/75 max-w-2xl">Réalisez des états des lieux, remises de clefs et visites pour des bailleurs de votre secteur. Vous choisissez vos missions, vos zones et vos disponibilités.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={scrollToForm} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold transition-colors" data-testid="referent-hero-cta">
                Proposer ma candidature <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/gestion-locative-a-distance" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white hover:bg-white/10 font-semibold transition-colors">
                En savoir plus
              </Link>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
          <h2 className="font-display text-3xl font-semibold text-brand-greenDeep text-center">Pourquoi devenir référent ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {BENEFITS.map((b) => (
              <div key={b.title} className="rounded-3xl border border-brand-soft p-6">
                <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><b.icon className="w-5 h-5" /></div>
                <p className="font-semibold text-brand-greenDeep mt-3">{b.title}</p>
                <p className="text-sm text-brand-green/65 mt-1 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Profiles */}
        <section className="bg-brand-softer py-16">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
            <h2 className="font-display text-3xl font-semibold text-brand-greenDeep">Qui peut devenir référent ?</h2>
            <p className="mt-3 text-brand-green/70">Tous les profils sérieux et disponibles sont les bienvenus.</p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              {PROFILES.map((p) => (
                <span key={p} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-brand-soft text-sm font-medium text-brand-greenDeep"><BadgeCheck className="w-4 h-4 text-brand-limeDark" /> {p}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="max-w-5xl mx-auto px-5 lg:px-8 py-16">
          <h2 className="font-display text-3xl font-semibold text-brand-greenDeep text-center">Comment ça marche ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-3xl border border-brand-soft p-6">
                <div className="w-9 h-9 rounded-full bg-brand-greenDeep text-brand-lime font-bold flex items-center justify-center">{s.n}</div>
                <p className="font-semibold text-brand-greenDeep mt-3">{s.title}</p>
                <p className="text-sm text-brand-green/65 mt-1 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Application form */}
        <section id="candidature" className="bg-brand-softer py-16 scroll-mt-24">
          <div className="max-w-2xl mx-auto px-5 lg:px-8">
            <ReferentApplyForm />
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
          <h2 className="font-display text-3xl font-semibold text-brand-greenDeep mb-6">Questions fréquentes</h2>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group bg-white rounded-2xl border border-brand-soft p-5" data-testid={`referent-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer font-semibold text-brand-greenDeep list-none [&::-webkit-details-marker]:hidden">
                  {f.q}
                  <span className="text-brand-green/40 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-brand-green/75 text-[15px] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
